import React, { useEffect, useState } from 'react';
import { ArrowDownToLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { usdtService } from '../../services/usdtService';
import {
  createMockTxHash,
  sendUsdtTransfer,
} from '../../services/usdtContract';
import { formatMoney } from '../../utils/formatMoney';

export function UsdtDepositForm() {
  const { isAuthenticated } = useAuth();
  const { refreshWallet } = useWallet();
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    usdtService.getConfig().then((data) => {
      setConfig(data);
      setAmount(data.presets?.[1] ?? data.presets?.[0] ?? 50);
    });
  }, [isAuthenticated]);

  const handlePreset = (preset) => {
    setAmount(preset);
    setCustomAmount('');
  };

  const handleCustomChange = (event) => {
    const value = event.target.value.replace(/[^\d.]/g, '');
    setCustomAmount(value);
    if (value) setAmount(Number(value));
  };

  const handleDeposit = () => {
    if (!config || !amount || amount < config.minDeposit) {
      setMessage(`ฝากขั้นต่ำ ${formatMoney(config?.minDeposit ?? 1)}`);
      return;
    }

    if (!config.mockMode && config.depositReady === false) {
      setMessage('ระบบฝากยังไม่พร้อม กรุณาติดต่อแอดมิน');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    const transferPromise = config.mockMode
      ? Promise.resolve(createMockTxHash())
      : sendUsdtTransfer({
          contractAddress: config.contractAddress,
          depositAddress: config.depositAddress,
          amount,
          decimals: config.decimals,
          chainId: config.chainId,
          minConfirmations: config.minConfirmations,
        });

    transferPromise
      .then((txHash) =>
        config.mockMode
          ? usdtService.confirmDeposit(txHash, amount)
          : usdtService.confirmDepositWithRetry(txHash, amount)
      )
      .then(() => {
        setMessage(`ฝาก ${formatMoney(amount)} สำเร็จ!`);
        setCustomAmount('');
        setAmount(config.presets?.[1] ?? config.presets?.[0] ?? 50);
        return refreshWallet();
      })
      .catch((error) => {
        setMessage(usdtService.getErrorMessage(error) || 'ฝากเงินไม่สำเร็จ');
      })
      .finally(() => setIsSubmitting(false));
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-600">
          กรุณา Connect Wallet ก่อนฝากเงิน
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-gold/40">
      <CardHeader className="bg-brand-gold-light/20">
        <CardTitle className="flex items-center gap-2 text-brand-red">
          <ArrowDownToLine className="h-5 w-5" />
          ฝากเงิน
        </CardTitle>
        <p className="mt-1 text-sm text-gray-600">
          โอนจากกระเป๋า Digital ของคุณเข้ากระเป๋าเว็บไซต์
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {config?.mockMode ? (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            โหมดทดสอบ — ไม่ต้องโอนจริง
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {(config?.presets ?? [10, 50, 100, 500, 1000]).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePreset(preset)}
              className={`rounded-xl border-2 py-3 text-center text-sm font-semibold transition-all ${
                amount === preset && !customAmount
                  ? 'border-brand-red bg-brand-gold-light text-brand-red'
                  : 'border-gray-200 bg-white hover:border-brand-gold'
              }`}
            >
              {formatMoney(preset)}
            </button>
          ))}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            จำนวนกำหนดเอง
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={customAmount}
            onChange={handleCustomChange}
            placeholder="ระบุจำนวน เช่น 50"
            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 focus:border-brand-red focus:outline-none"
          />
        </div>

        <div className="rounded-xl bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600">จำนวนที่จะฝาก</p>
          <p className="text-3xl font-bold text-brand-gold">
            {formatMoney(amount)}
          </p>
        </div>

        {message ? (
          <div
            className={`rounded-lg p-3 text-center text-sm ${
              message.includes('สำเร็จ')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-brand-red'
            }`}
          >
            {message}
          </div>
        ) : null}

        <Button
          size="lg"
          className="w-full"
          disabled={isSubmitting || !config}
          onClick={handleDeposit}
        >
          {isSubmitting ? 'กำลังฝาก...' : `ฝาก ${formatMoney(amount)}`}
        </Button>
      </CardContent>
    </Card>
  );
}
